// Web platform adapter — the browser build's implementation of StenalApi.
//
// The Electron build exposes window.stenal from a preload script backed by IPC
// to the Node main process. The browser has no main process, so this module
// provides the same surface (see src/renderer/platform.ts) by:
//   • proxying the secret-bearing / Node-only operations (catalog, translate,
//     AI heater import, DWG→DXF) to the lightweight server over HTTP;
//   • doing everything else with browser APIs — file open/save via <input> and
//     Blob downloads, printing / PDF export via the print dialog, settings in
//     localStorage.
// It is injected as window.stenalWeb and picked up by platform.ts. Importing
// this module for its side effect (assigning window.stenalWeb) MUST happen
// before the renderer's main.ts is evaluated (see the web entry point).

import type { StenalApi } from './platform';
import { openDialog } from './dialog';
import {
  tr,
  trMessage,
} from './i18n';
import type {
  AppSettings,
  BenchSlatPreset,
  CadUser,
  Cartiglio,
  ChangelogEntry,
  DeletedProduct,
  Drawing,
  ImportHeaterResult,
  ImportProductResult,
  ListProjectsQuery,
  OpenDrawingResult,
  PipeType,
  ProductTemplate,
  ProductWriteResult,
  SavedColor,
  SavedProjectFull,
  SavedProjectPage,
  SavedProjectSummary,
  SaveProjectRequest,
  SteamBenchPreset,
} from '../shared/types';
import { mergePipeTypes } from '../shared/pipeTypes';

declare global {
  interface Window {
    /** Optional API/base origin for the server. Empty ⇒ same origin as the app. */
    STENAL_SERVER?: string;
  }
}

export const SERVER_BASE = (window.STENAL_SERVER || '').replace(/\/+$/, '');
const SESSION_KEY = 'stenal.session';
const SETTINGS_KEY = 'stenal.settings';

// ---- busy / error reporters (mirror the desktop's onBaserowBusy/onBaserowError) ----
const busyCbs: Array<(busy: boolean) => void> = [];
const errorCbs: Array<(msg: string) => void> = [];
let busyCount = 0;
function setBusy(delta: number): void {
  busyCount = Math.max(0, busyCount + delta);
  const busy = busyCount > 0;
  for (const cb of busyCbs) { try { cb(busy); } catch { /* ignore */ } }
}
function reportError(msg: string): void {
  for (const cb of errorCbs) { try { cb(msg); } catch { /* ignore */ } }
}

// ---- session token (issued by webAuth after a stenal.net login) ----
/** Current bearer token from the stored session, or '' if signed out. */
export function getSessionToken(): string {
  try {
    const s = JSON.parse(localStorage.getItem(SESSION_KEY) || '{}') as { token?: unknown };
    return typeof s.token === 'string' ? s.token : '';
  } catch { return ''; }
}
export function clearSession(): void { try { localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ } }

// Truthy in the same shapes the server accepts (a session stored before the
// server normalised the flag could still hold a string like "yes").
function claimTrue(v: unknown): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v === 1;
  if (typeof v === 'string') { const s = v.trim().toLowerCase(); return s === 'true' || s === 'yes' || s === 'y' || s === '1' || s === 'on'; }
  return false;
}

/** Whether the signed-in user may edit shared standards (the `edit` claim from
 *  stenal.net). Read straight from the stored session so no import of webAuth is
 *  needed. Signed out ⇒ false. */
export function canEditStandards(): boolean {
  try {
    const s = JSON.parse(localStorage.getItem(SESSION_KEY) || '{}') as { user?: { edit?: unknown } };
    return claimTrue(s.user?.edit);
  } catch { return false; }
}

/** The signed-in user's stenal.net id (for owner-vs-member UI decisions), read
 *  straight from the stored session. '' when signed out. */
export function currentUserId(): string {
  try {
    const s = JSON.parse(localStorage.getItem(SESSION_KEY) || '{}') as { user?: { id?: unknown } };
    return typeof s.user?.id === 'string' ? s.user.id : '';
  } catch { return ''; }
}

/** The signed-in user's display name (Bubble `nome`), read straight from the
 *  stored session. '' when signed out or unnamed. */
export function currentUserName(): string {
  try {
    const s = JSON.parse(localStorage.getItem(SESSION_KEY) || '{}') as { user?: { nome?: unknown } };
    return typeof s.user?.nome === 'string' ? s.user.nome : '';
  } catch { return ''; }
}

/** The signed-in user's area-manager id, read straight from the stored session.
 *  '' when they have none. `saveProject` auto-adds it to the acl of everything
 *  they create, so it is access nobody granted by hand. */
export function currentUserAreaManager(): string {
  try {
    const s = JSON.parse(localStorage.getItem(SESSION_KEY) || '{}') as { user?: { areamanager?: unknown } };
    return typeof s.user?.areamanager === 'string' ? s.user.areamanager : '';
  } catch { return ''; }
}

/** Whether the signed-in user has the `commerciale` claim (sees every user's
 *  projects), read straight from the stored session. False when signed out. */
export function isCommerciale(): boolean {
  try {
    const s = JSON.parse(localStorage.getItem(SESSION_KEY) || '{}') as { user?: { commerciale?: unknown } };
    return claimTrue(s.user?.commerciale);
  } catch { return false; }
}

// On a 401 mid-session (token lapsed / access revoked) the adapter clears the
// session and asks webAuth to re-run its login gate, then retries once. webAuth
// registers this so webPlatform doesn't import it (avoids a circular module).
let reauth: (() => Promise<void>) | null = null;
export function setReauthHandler(fn: () => Promise<void>): void { reauth = fn; }

/** One authenticated request to the server. Reports busy/error like the desktop
 *  and re-authenticates once on a 401. Returns the parsed JSON. */
/** A request to the server. A status in `accept` is an answer the caller reads
 *  (a refused catalogue write), not an error. */
async function req<T>(path: string, init: RequestInit = {}, retry = true, accept: readonly number[] = []): Promise<T> {
  setBusy(1);
  try {
    const token = getSessionToken();
    const headers = new Headers(init.headers || {});
    if (token) headers.set('Authorization', `Bearer ${token}`);
    if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    let res: Response;
    try {
      res = await fetch(`${SERVER_BASE}${path}`, { ...init, headers });
    } catch (err) {
      const msg = tr('Could not reach the StenalCAD server: {reason}').replace('{reason}', (err as Error).message);
      reportError(msg);
      throw new Error(msg);
    }
    if (res.status === 401 && retry) {
      clearSession();
      if (reauth) { await reauth(); return req<T>(path, init, false, accept); }
    }
    const text = await res.text();
    const data = text ? safeJson(text) : undefined;
    if (!res.ok && !accept.includes(res.status)) {
      let msg = tr('Server error (HTTP {n}).').replace('{n}', String(res.status));
      if (data && typeof data === 'object' && 'error' in data) {
        const e = (data as { error?: unknown }).error;
        if (typeof e === 'string' && e) msg = trMessage(e);
      }
      reportError(msg);
      throw new Error(msg);
    }
    return data as T;
  } finally {
    setBusy(-1);
  }
}
function safeJson(text: string): unknown { try { return JSON.parse(text); } catch { return text; } }

/** A catalogue write: the server's answer, a refusal (409) or a failure (502)
 *  included, for the caller to show; out of reach, a failure too. */
async function productWrite(path: string, init: RequestInit): Promise<ProductWriteResult> {
  try {
    const r = await req<ProductWriteResult | undefined>(path, init, true, [409, 502]);
    return r && typeof r === 'object' && 'ok' in r ? r : { ok: true };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

// ---- browser file helpers ----
function pickFile(accept: string): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.style.display = 'none';
    // `cancel` fires in modern browsers; fall back to a focus-based timeout.
    let done = false;
    const finish = (f: File | null): void => { if (done) return; done = true; input.remove(); resolve(f); };
    input.addEventListener('change', () => finish(input.files && input.files[0] ? input.files[0] : null));
    input.addEventListener('cancel', () => finish(null));
    document.body.appendChild(input);
    input.click();
  });
}
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
function fileToDataUrl(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}
async function fileToBase64(file: File | Blob): Promise<string> {
  const dataUrl = await fileToDataUrl(file);
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}
function extOf(name: string): string { const m = /\.([a-z0-9]+)$/i.exec(name); return m ? m[1].toLowerCase() : ''; }
function baseName(name: string): string { return name.replace(/\.[^.]+$/, ''); }

/** Convert DWG bytes to DXF via the server's converter endpoint. */
async function dwgBlobToDxf(blob: Blob, fileName: string): Promise<string> {
  const form = new FormData();
  form.append('file', blob, fileName);
  const { dxfText } = await req<{ dxfText: string }>('/api/convert/dwg', { method: 'POST', body: form });
  return dxfText;
}

/** Read a picked drawing file into the same shape the desktop's drawing:open /
 *  product:importFile return. DWG bytes are converted to DXF by the server. */
async function readDrawingFile(file: File): Promise<OpenDrawingResult & ImportProductResult> {
  const fileName = file.name;
  const ext = extOf(fileName);
  try {
    if (ext === 'pdf') {
      return { ok: true, fileName, pdfBase64: await fileToBase64(file) };
    }
    if (ext === 'png' || ext === 'jpg' || ext === 'jpeg') {
      return { ok: true, fileName, imageDataUrl: await fileToDataUrl(file) };
    }
    if (ext === 'dwg') {
      return { ok: true, fileName, dxfText: await dwgBlobToDxf(file, fileName) };
    }
    // DXF (or anything else) — read as text.
    const dxfText = await file.text();
    return { ok: true, fileName, dxfText };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

const DRAWING_ACCEPT = '.dxf,.dwg,.pdf,.png,.jpg,.jpeg';

// ---- three-way choices (the web twins of Electron's dialog.showMessageBox) ----
// Built on the one dialog: Cancel first, the recommended action last (Enter),
// Esc cancels, a backdrop click changes nothing.
function askThree(title: string, message: string, labels: [string, string], cancelValue: number): Promise<number> {
  return new Promise((resolve) => {
    const done = (v: number): void => { d.close(); resolve(v); };
    const d = openDialog({
      title,
      message,
      buttons: [
        { label: 'Cancel', role: 'cancel', onClick: () => done(cancelValue) },
        { label: labels[1], onClick: () => done(1) },
        { label: labels[0], role: 'confirm', primary: true, onClick: () => done(0) },
      ],
    });
  });
}

// 0 = include, 1 = exclude, 2 = cancel (matches dxf:askInclude).
function askInclude(): Promise<number> {
  return askThree('Export to DXF',
    `${tr('Include the imported DXF plan in the export?')}\n${tr('You can export just your drawing, or your drawing together with the imported DXF background.')}`,
    ['Include imported plan', 'Exclude it'], 2);
}

// Unsaved-changes prompt: 0 = Save, 1 = Don't Save, 2 = Cancel (matches the
// desktop 'project:confirmUnsaved' contract).
function askUnsaved(): Promise<number> {
  return askThree('Unsaved changes',
    `${tr('This drawing has unsaved changes.')}\n${tr('Do you want to save them before continuing?')}`,
    ['Save', "Don't Save"], 2);
}

// ---- printing / PDF export via a hidden iframe + the browser print dialog ----
function printHtml(html: string): Promise<boolean> {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    let settled = false;
    const cleanup = (): void => { setTimeout(() => iframe.remove(), 1000); };
    iframe.onload = () => {
      const win = iframe.contentWindow;
      if (!win) { if (!settled) { settled = true; cleanup(); resolve(false); } return; }
      const after = (): void => { if (settled) return; settled = true; cleanup(); resolve(true); };
      win.addEventListener('afterprint', after);
      // Fallback: some browsers never emit afterprint.
      setTimeout(after, 60000);
      try { win.focus(); win.print(); } catch { after(); }
    };
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;
    if (!doc) { resolve(false); return; }
    doc.open(); doc.write(html); doc.close();
  });
}
const esc = (s: string): string => s.replace(/[<>&"]/g, '').trim();
// Single-image page HTML — mirrors the desktop print:image handler.
function singlePageHtml(dataUrl: string, title: string, widthMm?: number, heightMm?: number): string {
  const exact = typeof widthMm === 'number' && typeof heightMm === 'number' && widthMm > 0 && heightMm > 0;
  const pageCss = exact ? `@page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }` : '@page { size: auto; margin: 10mm; }';
  const bodyCss = exact
    ? 'html,body { margin:0; padding:0; }'
    : 'html,body { margin:0; padding:0; height:100%; } body { display:flex; align-items:center; justify-content:center; overflow:hidden; }';
  const imgCss = exact ? `img { display:block; width:${widthMm}mm; height:${heightMm}mm; }` : 'img { max-width:100%; max-height:100%; display:block; }';
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>${pageCss}${bodyCss}${imgCss}</style></head><body><img src="${dataUrl}"></body></html>`;
}
// Multi-page (one sheet per page, mixed sizes) — mirrors print:images / pdf:exportAll.
function multiPageHtml(pages: Array<{ dataUrl: string; widthMm: number; heightMm: number }>, title = 'StenalCAD'): string {
  const key = (p: { widthMm: number; heightMm: number }): string => `${p.widthMm}x${p.heightMm}`;
  const sizes = [...new Set(pages.map(key))];
  const pageRules = sizes.map((k, i) => { const [w, h] = k.split('x'); return `@page p${i} { size: ${w}mm ${h}mm; margin: 0; }`; }).join('');
  const nameRules = sizes.map((_k, i) => `.p${i} { page: p${i}; }`).join('');
  const body = pages.map((p) => `<div class="sheet p${sizes.indexOf(key(p))}"><img src="${p.dataUrl}" style="width:${p.widthMm}mm;height:${p.heightMm}mm;display:block"></div>`).join('');
  const css = pageRules + nameRules + 'html,body{margin:0;padding:0;}.sheet{break-after:page;page-break-after:always;}.sheet:last-child{break-after:auto;page-break-after:auto;}';
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>${css}</style></head><body>${body}</body></html>`;
}

// ---- settings (localStorage) ----
function loadSettings(): AppSettings {
  try { return JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}') as AppSettings; } catch { return {}; }
}

const webPlatform: StenalApi = {
  // ---- file open / import (browser <input type=file>) ----
  async openDrawing(): Promise<OpenDrawingResult> {
    const file = await pickFile(DRAWING_ACCEPT);
    if (!file) return { ok: false, error: 'cancelled' };
    return readDrawingFile(file);
  },
  async importProductFile(): Promise<ImportProductResult> {
    const file = await pickFile(DRAWING_ACCEPT);
    if (!file) return { ok: false, error: 'cancelled' };
    return readDrawingFile(file);
  },
  dwgToDxf: (bytes: ArrayBuffer, fileName: string): Promise<string> => dwgBlobToDxf(new Blob([bytes]), fileName),

  canEditStandards,

  // ---- catalog / shared data (server, Baserow-backed) ----
  listProducts: () => req<ProductTemplate[]>('/api/products'),
  async saveProduct(product: ProductTemplate, base: string | null): Promise<ProductWriteResult> {
    return productWrite(`/api/products/${encodeURIComponent(product.id)}`, { method: 'PUT', body: JSON.stringify({ product, base }) });
  },
  async deleteProduct(id: string, base: string | null): Promise<ProductWriteResult> {
    return productWrite(`/api/products/${encodeURIComponent(id)}${base ? `?base=${encodeURIComponent(base)}` : ''}`, { method: 'DELETE' });
  },
  listDeletedProducts: () => req<DeletedProduct[]>('/api/products/deleted'),
  productChangelog: (productId: string) => req<ChangelogEntry[]>(`/api/products/${encodeURIComponent(productId)}/changelog`),
  listColors: () => req<SavedColor[]>('/api/colors'),
  saveColor: (color: SavedColor) => req<SavedColor>('/api/colors', { method: 'POST', body: JSON.stringify(color) }),
  async deleteColor(id: number): Promise<boolean> { await req(`/api/colors/${id}`, { method: 'DELETE' }); return true; },
  listCartigli: () => req<Cartiglio[]>('/api/cartigli'),
  saveCartiglio: (c: Cartiglio) => req<Cartiglio>('/api/cartigli', { method: 'POST', body: JSON.stringify(c) }),
  async deleteCartiglio(rowId: number): Promise<boolean> { await req(`/api/cartigli/${rowId}`, { method: 'DELETE' }); return true; },
  listBenchPresets: () => req<BenchSlatPreset[]>('/api/bench-presets'),
  saveBenchPreset: (p: BenchSlatPreset) => req<BenchSlatPreset>('/api/bench-presets', { method: 'POST', body: JSON.stringify(p) }),
  async deleteBenchPreset(rowId: number): Promise<boolean> { await req(`/api/bench-presets/${rowId}`, { method: 'DELETE' }); return true; },
  listSteamBenchPresets: () => req<SteamBenchPreset[]>('/api/steam-bench-presets'),
  saveSteamBenchPreset: (p: SteamBenchPreset) => req<SteamBenchPreset>('/api/steam-bench-presets', { method: 'POST', body: JSON.stringify(p) }),
  listSaunaProfiles: () => req<SteamBenchPreset[]>('/api/sauna-profiles'),
  saveSaunaProfile: (p: SteamBenchPreset) => req<SteamBenchPreset>('/api/sauna-profiles', { method: 'POST', body: JSON.stringify(p) }),
  async listPipeTypes(): Promise<PipeType[]> { return mergePipeTypes(await req<PipeType[]>('/api/pipe-types')); },
  savePipeType: (t: PipeType) => req<PipeType>('/api/pipe-types', { method: 'POST', body: JSON.stringify(t) }),
  async deletePipeType(rowId: number): Promise<boolean> { await req(`/api/pipe-types/${rowId}`, { method: 'DELETE' }); return true; },

  // ---- secret proxies ----
  async translate(text: string, from: string, to: string): Promise<string> {
    const { text: out } = await req<{ text: string }>('/api/translate', { method: 'POST', body: JSON.stringify({ text, from, to }) });
    return out;
  },
  async importHeater(url: string): Promise<ImportHeaterResult> {
    const apiKey = loadSettings().anthropicApiKey || '';
    try { return await req<ImportHeaterResult>('/api/ai/heater', { method: 'POST', body: JSON.stringify({ url, apiKey }) }); }
    catch (err) { return { ok: false, error: (err as Error).message }; }
  },

  // ---- settings (localStorage; BYO Anthropic key never leaves the browser except per-request) ----
  async getSettings(): Promise<AppSettings> { return loadSettings(); },
  async saveSettings(patch: Partial<AppSettings>): Promise<AppSettings> {
    const next = { ...loadSettings(), ...patch };
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  },

  // ---- project save / open (browser download + file picker) ----
  async saveProject(drawing: Drawing): Promise<string | null> {
    const name = (drawing.fileName || 'project').replace(/[/\\:*?"<>|]/g, '_');
    downloadBlob(new Blob([JSON.stringify(drawing)], { type: 'application/json' }), `${name}.stenal`);
    return name;
  },
  async openProject(): Promise<{ drawing: Drawing; name: string } | null> {
    const file = await pickFile('.stenal,.json');
    if (!file) return null;
    try {
      const drawing = JSON.parse(await file.text()) as Drawing;
      return { drawing, name: baseName(file.name) };
    } catch { return null; }
  },
  confirmUnsaved: () => askUnsaved(),

  // ---- Cloud projects (server-mediated) ----
  listCloudProjects: (q: ListProjectsQuery = {}) => {
    const params = new URLSearchParams();
    if (q.page) params.set('page', String(q.page));
    if (q.size) params.set('size', String(q.size));
    if (q.search) params.set('search', q.search);
    if (q.creator) params.set('creator', q.creator);
    const qs = params.toString();
    return req<SavedProjectPage>(`/api/projects${qs ? '?' + qs : ''}`);
  },
  loadCloudProject: (rowId: number) => req<SavedProjectFull>(`/api/projects/${rowId}`),
  saveCloudProject: (payload: SaveProjectRequest) =>
    req<SavedProjectSummary>('/api/projects', { method: 'POST', body: JSON.stringify(payload) }),
  deleteCloudProject: (rowId: number) => req<void>(`/api/projects/${rowId}`, { method: 'DELETE' }),
  renameCloudProject: (rowId: number, name: string) =>
    req<SavedProjectSummary>(`/api/projects/${rowId}/rename`, { method: 'POST', body: JSON.stringify({ name }) }),
  listCadUsers: () => req<CadUser[]>('/api/cad-users'),
  shareCloudProject: (rowId: number, userIds: string[]) =>
    req<SavedProjectSummary>(`/api/projects/${rowId}/share`, { method: 'POST', body: JSON.stringify({ userIds }) }),
  unshareCloudProject: (rowId: number, userIds: string[]) =>
    req<SavedProjectSummary>(`/api/projects/${rowId}/unshare`, { method: 'POST', body: JSON.stringify({ userIds }) }),
  currentUserId,
  currentUserName,
  currentUserAreaManager,
  isCommerciale,

  // ---- DXF / 3D model export (browser download) ----
  askDxfInclude: () => askInclude(),
  async exportDxf(dxf: string, defaultName: string): Promise<string | null> {
    const name = (defaultName || 'drawing').replace(/[/\\:*?"<>|]/g, '_');
    downloadBlob(new Blob([dxf], { type: 'application/dxf' }), `${name}.dxf`);
    return name;
  },
  async exportModel3d(data: Uint8Array | string, defaultName: string, ext: 'glb' | 'obj' | 'dxf' | 'stl' | 'usdz', mtl?: string): Promise<string | null> {
    const name = (defaultName || 'model').replace(/[/\\:*?"<>|]/g, '_');
    let payload = data;
    if (typeof payload === 'string' && ext === 'obj' && mtl) {
      // Ship the companion .mtl and repoint the OBJ's mtllib at it by name.
      const mtlName = `${name}.mtl`;
      payload = payload.replace(/^mtllib .*$/m, `mtllib ${mtlName}`);
      downloadBlob(new Blob([mtl], { type: 'text/plain' }), mtlName);
    }
    const blob = typeof payload === 'string'
      ? new Blob([payload], { type: 'text/plain' })
      : new Blob([payload as BlobPart], { type: ext === 'usdz' ? 'model/vnd.usdz+zip' : 'application/octet-stream' });
    downloadBlob(blob, `${name}.${ext}`);
    return name;
  },

  // ---- printing / PDF (browser print dialog; "Save as PDF" replaces silent export) ----
  printImage: (dataUrl, landscape, jobName, widthMm, heightMm) =>
    printHtml(singlePageHtml(dataUrl, jobName || 'StenalCAD', widthMm, heightMm)),
  printImages: (pages, jobName) =>
    pages && pages.length ? printHtml(multiPageHtml(pages, jobName || 'StenalCAD')) : Promise.resolve(false),
  async exportSheetPdf(dataUrl, widthMm, heightMm, _landscape, defaultName): Promise<string | null> {
    await printHtml(singlePageHtml(dataUrl, defaultName || 'sheet', widthMm, heightMm));
    return defaultName || 'sheet';
  },
  async exportAllPdf(pages, defaultName): Promise<string | null> {
    if (!pages || !pages.length) return null;
    await printHtml(multiPageHtml(pages, defaultName || 'sheets'));
    return defaultName || 'sheets';
  },

  // ---- renderer-local events ----
  // In the desktop build the OS menu fires menu:* IPC which preload forwards to
  // these callbacks. In the web build the in-app menu dispatches a DOM event of
  // the same channel name (see the in-app menu bar), which we bridge here.
  onMenu(channel: string, cb: () => void): void {
    window.addEventListener(`stenal-menu:${channel}`, () => cb());
  },
  onBaserowError(cb: (message: string) => void): void { errorCbs.push(cb); },
  onBaserowBusy(cb: (busy: boolean) => void): void { busyCbs.push(cb); },
  notifyReady(): void {
    // No main-process reveal handshake in the browser; drop any splash element.
    const splash = document.getElementById('splash');
    if (splash) splash.remove();
  },
};

window.stenalWeb = webPlatform;

export { webPlatform };
